import { studentApi } from '@/features/student/api';
import { useState, type CSSProperties, type FC } from 'react';
import type {
  TeacherAttestationEntry,
  TeacherDiscipline,
  TeacherGroup,
  TeacherProfile,
} from '../types';
import { BookOpenIcon, CheckIcon } from './TeacherIcons';

interface TeacherAttestationOverlayProps {
  disciplines: TeacherDiscipline[];
  selectedDisciplineCode: string;
  onSelectDiscipline: (code: string) => void;
  groups: TeacherGroup[];
  selectedGroupCode: string;
  onSelectGroup: (code: string) => void;
  attestations: TeacherAttestationEntry[];
  profile?: TeacherProfile | null;
  onBackToCabinet: () => void;
  onOpenCourseWorkspace: () => void;
  onGenerateStatement: (disciplineCode: string, groupCode: string) => Promise<void>;
}

function admissionColor(status: string): string {
  if (status === 'Допущен' || status === 'ДОПУЩЕН') return '#49ddb7';
  if (status === 'Не допущен') return '#ff7b65';
  return '#41d2ff';
}

export const TeacherAttestationOverlay: FC<TeacherAttestationOverlayProps> = ({
  disciplines,
  selectedDisciplineCode,
  onSelectDiscipline,
  groups,
  selectedGroupCode,
  onSelectGroup,
  attestations,
  profile,
  onBackToCabinet,
  onOpenCourseWorkspace,
  onGenerateStatement,
}) => {
  const [error, setError] = useState('');
  const [isGeneratedModalOpen, setIsGeneratedModalOpen] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const handleGenerate = async () => {
    setIsVerifying(true); setError('');
    try {
      await onGenerateStatement(selectedDisciplineCode, selectedGroupCode);
      setIsGeneratedModalOpen(true);
    } catch (e) { setError(e instanceof Error ? e.message : 'Не удалось сформировать ведомость');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="cab-shell cab-shell--wide">
      <div className="cab-top">
        <div className="cab-top-left">
          <div>
            <small>Кабинет преподавателя</small>
            <h2>Аттестация и ведомости</h2>
            <p>Контрольные точки дисциплины, допуск и формирование ведомостей.</p>
          </div>
        </div>
        <div className="cab-top-actions">
          <button type="button" className="cab-btn" onClick={onOpenCourseWorkspace}>
            <BookOpenIcon className="w-4 h-4" />
            Учебный процесс
          </button>
          <button type="button" className="cab-btn primary" onClick={onBackToCabinet}>
            В кабинет
          </button>
        </div>
      </div>

      <button disabled={!selectedDisciplineCode || !selectedGroupCode} onClick={() => void studentApi.downloadCourseFile(`/teacher/attestation/export?discipline_code=${encodeURIComponent(selectedDisciplineCode)}&group_code=${encodeURIComponent(selectedGroupCode)}`)}>Скачать сформированную ведомость CSV</button>
      <div className="cab-body">
        {error && <p role="alert">{error}</p>}
        <div className="cabinet-note" style={{ marginBottom: 14 }}>
          {profile?.full_name || '—'} · {profile?.department || '—'}
        </div>

        <div className="cabinet-stack" style={{ marginBottom: 14 }}>
          <div>
            <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 800, color: '#8faebb' }}>Дисциплина</p>
            <div className="cabinet-filters">
              {disciplines.map((d) => (
                <button
                  key={d.code}
                  type="button"
                  onClick={() => onSelectDiscipline(d.code)}
                  className={`cabinet-filter${selectedDisciplineCode === d.code ? ' active' : ''}`}
                >
                  {d.code} · {d.title}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 800, color: '#8faebb' }}>Группа</p>
            <div className="cabinet-filters">
              {groups.map((g) => (
                <button
                  key={g.group_code}
                  type="button"
                  onClick={() => onSelectGroup(g.group_code)}
                  className={`cabinet-filter${selectedGroupCode === g.group_code ? ' active' : ''}`}
                >
                  {g.group_code}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="cabinet-grid3" style={{ marginBottom: 14 }}>
          <article className="cabinet-card" style={{ ['--accent' as string]: '#e2ad3a' }}>
            <span className="cabinet-badge" style={{ '--badge': '#e2ad3a' } as CSSProperties}>
              7 неделя
            </span>
            <strong style={{ display: 'block', marginTop: 8 }}>Рубежный контроль 1</strong>
            <p style={{ margin: '6px 0 0', fontSize: 12 }}>Статус: запланировано</p>
          </article>
          <article className="cabinet-card" style={{ ['--accent' as string]: '#9e80ff' }}>
            <span className="cabinet-badge" style={{ '--badge': '#9e80ff' } as CSSProperties}>
              15 неделя
            </span>
            <strong style={{ display: 'block', marginTop: 8 }}>Рубежный контроль 2</strong>
            <p style={{ margin: '6px 0 0', fontSize: 12 }}>Статус: запланировано</p>
          </article>
          <article className="cabinet-card" style={{ ['--accent' as string]: '#49ddb7' }}>
            <span className="cabinet-badge" style={{ '--badge': '#49ddb7' } as CSSProperties}>
              после учебных недель
            </span>
            <strong style={{ display: 'block', marginTop: 8 }}>Итоговый контроль</strong>
            <p style={{ margin: '6px 0 0', fontSize: 12 }}>Статус: запланировано</p>
          </article>
        </div>

        <div className="cabinet-actions" style={{ marginBottom: 14 }}>
          <button type="button" className="cabinet-btn primary" onClick={handleGenerate} disabled={isVerifying}>
            {isVerifying ? 'Формирование…' : 'Сформировать ведомость'}
          </button>
          <button type="button" className="cabinet-btn" onClick={handleGenerate} disabled={isVerifying}>
            Проверить допуск
          </button>
        </div>

        <section className="cabinet-panel">
          <div className="cabinet-panel-body">
            <div className="cabinet-actions" style={{ marginBottom: 10, alignItems: 'center' }}>
              <span className="cabinet-badge" style={{ '--badge': '#49ddb7' } as CSSProperties}>
                {selectedGroupCode}
              </span>
              <strong>Список обучающихся</strong>
            </div>
            <div className="cabinet-stack">
              {attestations.map((item) => (
                <article
                  key={item.student_name}
                  className="cabinet-card"
                  style={{ ['--accent' as string]: admissionColor(item.admission_status) }}
                >
                  <div className="cabinet-actions" style={{ marginBottom: 8, alignItems: 'center' }}>
                    <strong>{item.student_name}</strong>
                    <span className="cabinet-spacer" />
                    <span
                      className="cabinet-badge"
                      style={{ '--badge': admissionColor(item.admission_status) } as CSSProperties}
                    >
                      {item.admission_status}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: '#8faebb' }}>
                    Посещаемость {item.attendance_rate}% · Текущий {item.current_score} · РК1{' '}
                    {item.rk1_score || '—'} · РК2 {item.rk2_score || '—'} · Итоговый {item.final_score || '—'}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </div>

      {isGeneratedModalOpen ? (
        <div className="cabinet-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cabinet-modal">
            <div className="cabinet-modal-head">
              <div>
                <div className="cabinet-actions" style={{ marginBottom: 6, alignItems: 'center' }}>
                  <CheckIcon className="w-4 h-4" />
                  <span className="cabinet-badge" style={{ '--badge': '#49ddb7' } as CSSProperties}>
                    Готово
                  </span>
                </div>
                <h2>Ведомость сформирована</h2>
              </div>
              <button
                type="button"
                className="cabinet-btn"
                onClick={() => setIsGeneratedModalOpen(false)}
                aria-label="Закрыть"
              >
                ✕
              </button>
            </div>
            <div className="cabinet-modal-body">
              <div className="cabinet-note ok">
                Электронная ведомость для группы <strong>{selectedGroupCode}</strong> по дисциплине{' '}
                <strong>{selectedDisciplineCode}</strong> успешно сформирована и верифицирована.
              </div>
              <p style={{ margin: '10px 0 0', fontSize: 12, color: '#8faebb' }}>
                Все обучающиеся с текущей успеваемостью выше пороговой и посещаемостью от 70% переведены в статус
                «Допущен».
              </p>
              <div className="cabinet-actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
                <button type="button" className="cabinet-btn primary" onClick={() => setIsGeneratedModalOpen(false)}>
                  Понятно
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
